import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken, HttpHeaders } from '@angular/common/http';

import { environment } from '@environments/environment'
import { Devices } from '@app/_models/devices';
import { MessageResponse } from '@app/_models/responses';
import { DeviceAccessRequest, DeviceAssociation } from '@app/_models/smart-device';
import { UserProfile } from '@app/_models/user';

export const DEVICE_REQUEST_CONTEXT = new HttpContextToken<boolean>(() => false);


@Injectable({ providedIn: 'root' })
export class DeviceService {
    constructor(private http: HttpClient) { }

    getSmartDevices() {
        return this.http.get<DeviceAssociation[]>(`${environment.apiUrl}/api/devices/user/associations`, this.smartDeviceOptions());
    }

    operateSmartDevice(request: { property_id: number; device_id: string; operation_type: string; status?: string; usage_duration: number; operation_time: number }) {
        return this.http.post<MessageResponse>(`${environment.apiUrl}/api/devices/operate`, request, this.smartDeviceOptions());
    }

    createSmartDevice(request: Record<string, unknown>) {
        return this.http.post<MessageResponse>(`${environment.apiUrl}/api/devices`, request, this.smartDeviceOptions());
    }

    requestSmartDeviceAccess(request: Record<string, unknown>) {
        return this.http.post<MessageResponse>(`${environment.apiUrl}/api/devices/associate`, request, this.smartDeviceOptions());
    }

    shareSmartDevice(deviceId: string, email: string) {
        return this.http.post<MessageResponse>(`${environment.apiUrl}/api/devices/associate/quick`, { deviceId, email, enabled: true }, this.smartDeviceOptions());
    }

    getSmartDeviceRequests(propertyId: number, unit: number) {
        return this.http.get<DeviceAccessRequest[]>(`${environment.apiUrl}/api/devices/requests/property/${propertyId}/unit/${unit}`, this.smartDeviceOptions());
    }

    approveSmartDeviceRequest(id: string | number) {
        return this.http.put<MessageResponse>(`${environment.apiUrl}/api/devices/requests/${encodeURIComponent(id)}/approve`, {}, this.smartDeviceOptions());
    }

    deleteSmartDeviceRequest(id: string | number) {
        return this.http.delete<MessageResponse>(`${environment.apiUrl}/api/devices/requests/${encodeURIComponent(id)}`, this.smartDeviceOptions());
    }

    getSmartDevicePremises(username: string) {
        return this.http.get<{ id: number; name: string; property_type?: string }[]>(`${environment.apiUrl}/api/user/property/subscriptions/${encodeURIComponent(username)}`, this.smartDeviceOptions());
    }

    getSmartDeviceAccess(username: string) {
        return this.http.get<{ property_id: number; unit_number: number }[]>(`${environment.apiUrl}/api/access/user/${encodeURIComponent(username)}`, this.smartDeviceOptions());
    }

    getSmartDeviceProfile(username: string) {
        return this.http.get<UserProfile>(`${environment.apiUrl}/api/userprofile/${encodeURIComponent(username)}`, this.smartDeviceOptions());
    }

    private smartDeviceOptions() {
        return { context: new HttpContext().set(DEVICE_REQUEST_CONTEXT, true) };
    }


    getDevices(propertyid: number) {
        return this.http.get<Devices[]>(`${environment.apiUrl}/api/community/gates/`+propertyid);
    }

    getUserDevices(username: string) {
        return this.http.get<Devices[]>(`${environment.apiUrl}/api/user/device/subscriptions/`+username);
    }

    operateDevice(request: any) {
        return this.http.post<any>(`${environment.apiUrl}/api/gates/operate`, request);
    }

    operateDeviceOffline(baseurl: string, request: any) {
        return this.http.post<any>(`${baseurl}/device/operate`, request);
    }

    getDeviceConfiguration() {
        return this.http.get<any>(`${environment.apiUrl}/api/property/configuration`);
    }

    //Calling method services below
    getCellularNetworks() {
        return this.http.get<any>(`${environment.apiUrl}/api/calling/network`);
    }

    getAllCallingDevices() {
        return this.http.get<any>(`${environment.apiUrl}/api/calling/device`);
    }

    getCallingDeviceByProperty(propertyid: number) {
        return this.http.get<any>(`${environment.apiUrl}/api/calling/device/property/` + propertyid);
    }

    getCallingDeviceById(deviceid: number) {
        return this.http.get<any>(`${environment.apiUrl}/api/calling/device/`+deviceid);
    }

    createPhoneNumber(request: any) {
        return this.http.post<any>(`${environment.apiUrl}/api/calling/phonenumber`, request);
    }

    updatePhoneNumber(request: any) {
        let recordid = request.id;

        let header = new HttpHeaders();
        header = header.append('content-type', 'application/json');

        return this.http.put<any>(`${environment.apiUrl}/api/calling/phonenumber/` + recordid, request, { headers : header});
    }

    deletePhoneNumber(id: number) {
        return this.http.delete<MessageResponse>(`${environment.apiUrl}/api/calling/phonenumber/` + id);
    }

    updatePhoneNumberStatus(request: any) {
        let recordid = request;

        let header = new HttpHeaders();
        header = header.append('content-type', 'application/json');

        return this.http.put<any>(`${environment.apiUrl}/api/calling/phonenumber/status/` + recordid, {}, { headers : header});
    }

    getAllPhoneNumbers() {
        return this.http.get<any>(`${environment.apiUrl}/api/calling/phonenumber`);
    }

    getPhoneNumbersByPropertyId(propertyid: number) {

        return this.http.get<any>(`${environment.apiUrl}/api/calling/phonenumber/property/` + propertyid);
    }
}
